"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DataSection = void 0;
const ui_1 = require("@app/component/ui");
const data_grid_1 = require("@app/component/ui/data-grid");
const forms_1 = require("@app/component/ui/forms");
const checkbox_1 = require("@app/component/ui/forms/checkbox/checkbox");
const dropdown_input_1 = require("@app/component/ui/forms/dropdown-input/dropdown-input");
const dropdown_multi_input_1 = require("@app/component/ui/forms/dropdown-multi-input/dropdown-multi-input");
const input_label_1 = require("@app/component/ui/forms/input-label/input-label");
const text_input_1 = require("@app/component/ui/forms/text-input/text-input");
const const_1 = require("@app/const");
const graphql_1 = require("@app/gql-types/graphql");
const custom_attribute_data_1 = require("@app/reducer/custom-attribute-data");
const user_details_1 = require("@app/reducer/user-details");
const request_1 = require("@app/request");
const email_check_public_1 = require("@app/request/email-check-public");
const store_1 = require("@app/store");
const util_1 = require("@app/util");
const maybe_unwrap_1 = require("@app/util/apollo/maybe-unwrap");
const builder_1 = require("@app/util/builders/builder");
const use_plugin_1 = require("@app/util/hooks/use-plugin");
const use_modal_1 = require("@app/util/modal/use-modal");
const moment_1 = __importDefault(require("moment"));
const react_1 = __importDefault(require("react"));
const react_hook_form_1 = require("react-hook-form");
const react_hot_toast_1 = __importDefault(require("react-hot-toast"));
const email_is_default_1 = require("../../util/email-is-default");
const employee_card_context_1 = require("../../util/employee-card-context");
const get_first_or_undefined_1 = require("../../util/get-first-or-undefined");
const useEmployeeCardFooterContent_1 = require("../../util/hooks/useEmployeeCardFooterContent");
const useUserDetailsCustomAttributes_1 = require("../../util/hooks/useUserDetailsCustomAttributes");
const useUserDetailsCustomData_1 = require("../../util/hooks/useUserDetailsCustomData");
const ToggleEdit = ({ type, editing, setEditing, afterTrue, hidden, disabled }) => {
    if (editing.includes(type)) {
        return null;
    }
    if (hidden) {
        return null;
    }
    return (react_1.default.createElement("div", { onClick: () => {
            if (disabled)
                return;
            setEditing((prev) => [...prev, type]);
            afterTrue?.();
        }, style: {
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'center',
            cursor: 'pointer',
        } },
        react_1.default.createElement(ui_1.Icon, { color: 'primary', disabled: disabled, ico: 'edit', size: 'small' }),
        react_1.default.createElement(ui_1.Text, { color: disabled ? 'gray-light' : 'primary', type: 'header', weight: 'bold' }, (0, util_1.t)('EDIT'))));
};
const DataSection = () => {
    const { employee, workspace, userIsMe } = (0, employee_card_context_1.useEmployeeCard)();
    const { closeModal } = (0, use_modal_1.useModal)();
    const [confirmEmail, setConfirmEmail] = react_1.default.useState('');
    const userAttributes = workspace?.userAttributes ?? [];
    const [editing, setEditing] = react_1.default.useState([]);
    const [customAttributes, updateCustomAttributes] = (0, useUserDetailsCustomAttributes_1.useUserDetailsCustomAttributes)();
    const { canWrite } = (0, util_1.useAccessRole)();
    const mappedCustomAttributes = react_1.default.useMemo(() => userAttributes.map((attr) => {
        return {
            name: attr?.name,
            attributeId: attr?.id,
            type: attr?.type,
            value: customAttributes?.find((customAttr) => customAttr.attributeId === attr?.id)?.value,
            values: attr?.options?.values?.map((v) => ({
                label: v,
                value: v,
            })),
        };
    }), [customAttributes, workspace?.userAttributes]);
    const columns = mappedCustomAttributes.reduce((acc, curr, index) => {
        if (index % 2 === 0) {
            acc.left.push(curr);
            return acc;
        }
        acc.right.push(curr);
        return acc;
    }, {
        left: [],
        right: [],
    });
    const [customData, updateCustomData] = (0, useUserDetailsCustomData_1.useUserDetailsCustomData)();
    const allowEditBasicFields = (canWrite(const_1.PERMISSIONS.WORKSPACE.EMPLOYEES) && !customData?.managedByPlugin) ||
        userIsMe;
    const allowEdit = canWrite(const_1.PERMISSIONS.WORKSPACE.EMPLOYEES) && !customData?.managedByPlugin;
    const keepFieldsDisabled = !allowEdit;
    const keepBasicFieldsDisabled = !allowEditBasicFields;
    const dispatch = (0, store_1.useRootDispatch)();
    const processedData = {
        firstName: (0, get_first_or_undefined_1.getFirstOrUndefined)([
            (0, maybe_unwrap_1.unwrapMaybe)(customData?.firstName),
            (0, maybe_unwrap_1.unwrapMaybe)(employee?.user?.firstName),
        ]),
        lastName: (0, get_first_or_undefined_1.getFirstOrUndefined)([
            (0, maybe_unwrap_1.unwrapMaybe)(customData?.lastName),
            (0, maybe_unwrap_1.unwrapMaybe)(employee?.user?.lastName),
        ]),
        email: (0, maybe_unwrap_1.unwrapMaybe)(employee?.user?.email),
        birthdate: (0, get_first_or_undefined_1.getFirstOrUndefined)([
            (0, maybe_unwrap_1.unwrapMaybe)(customData?.birthdate),
            (0, maybe_unwrap_1.unwrapMaybe)(employee?.user?.birthdate),
        ]),
        telephone: (0, get_first_or_undefined_1.getFirstOrUndefined)([
            (0, maybe_unwrap_1.unwrapMaybe)(customData?.telephone === '' ? undefined : customData?.telephone),
            (0, maybe_unwrap_1.unwrapMaybe)(employee?.user?.telephone),
        ]),
    };
    const { control, handleSubmit, setValue, formState: { isSubmitting }, } = (0, react_hook_form_1.useForm)({
        defaultValues: {
            basicFields: {
                lastName: processedData.lastName,
                firstName: processedData.firstName,
                email: processedData.email,
                telephone: processedData.telephone,
                birthdate: processedData.birthdate,
                address: (0, maybe_unwrap_1.unwrapMaybe)(customData?.address),
                employeeId: (0, maybe_unwrap_1.unwrapMaybe)(customData?.employeeId),
            },
            // @ts-expect-error Type '{ name: Maybe<string> | undefined; attributeId: Maybe<string> | undefined; type: Maybe<CustomAttributeValueType> | undefined; value: any; values: DropdownOption<...>[] | undefined; }[]' is not assignable to type '({ attributeId?: string | undefined; value?: string | number | boolean | (string | undefined)[] | undefined; type?: CustomAttributeValueType | undefined; values?: ({ ...; } | undefined)[] | undefined; } | undefined)[]'.
            customAttributes: mappedCustomAttributes,
        },
        mode: 'onChange',
        reValidateMode: 'onChange',
    });
    react_1.default.useEffect(() => {
        if ((0, util_1.isUndefined)(customData))
            return;
        // Don't overwrite the form while the user is editing — processedData is a
        // new object every render, so without this guard the effect would fire on
        // each re-render and reset fields like a freshly typed email.
        if (editing.includes('basicFields'))
            return;
        setValue('basicFields', {
            lastName: processedData.lastName,
            firstName: processedData.firstName,
            email: processedData.email,
            telephone: processedData.telephone,
            birthdate: processedData.birthdate,
            address: (0, maybe_unwrap_1.unwrapMaybe)(customData?.address),
            employeeId: (0, maybe_unwrap_1.unwrapMaybe)(customData?.employeeId),
        });
    }, [customData, editing]);
    const { isPreventedByPlugin,
    // isPluginEnabled
     } = (0, use_plugin_1.usePlugin)();
    const onSubmit = async (data) => {
        const id = employee?.user?.id;
        if ((0, util_1.isUndefined)(id)) {
            throw new Error('data-section@onSubmit: id cannot be undefined');
        }
        if (editing.includes('customAttributes')) {
            const customAttributes = data.customAttributes
                .map((attr) => ({
                attributeId: attr.attributeId,
                value: attr.value,
            }))
                .filter((ca) => (0, util_1.isDefined)(ca.value) && (0, util_1.isNotNull)(ca.value));
            void updateCustomAttributes(customAttributes);
            customAttributes.forEach((ca) => {
                const uid = id;
                if ((0, util_1.isUndefined)(uid))
                    return;
                dispatch((0, custom_attribute_data_1.updatedCustomAttributeData)({ ...ca, userId: uid }));
            });
        }
        if (editing.includes('basicFields')) {
            if (userIsMe) {
                (0, request_1.requestUserUpdate)({
                    id,
                    data: {
                        firstName: data.basicFields.firstName,
                        lastName: data.basicFields.lastName,
                        telephone: data.basicFields.telephone,
                        // Email is updated separately below
                        birthdate: data.basicFields.birthdate,
                    },
                });
            }
            else {
                void updateCustomData({
                    lastName: data.basicFields.lastName,
                    firstName: data.basicFields.firstName,
                    // Email is updated differently
                    telephone: data.basicFields.telephone,
                    address: data.basicFields.address,
                    birthdate: data.basicFields.birthdate,
                    employeeId: data.basicFields.employeeId,
                });
            }
            // if email was changed
            if (data?.basicFields?.email &&
                data?.basicFields?.email !== processedData.email) {
                // TODO: call userUpdate mutation to change the email here, but first check if the email is available by calling emailCheck query
                const emailAvailable = await (0, email_check_public_1.requestEmailCheckPublicTS)(data?.basicFields?.email);
                if (emailAvailable) {
                    react_hot_toast_1.default.custom(() => (react_1.default.createElement(ui_1.Alert, { type: ui_1.Alert.TYPES.WARNING, text: (0, util_1.t)('ERR_MESSAGE.emailAlreadyUsedByAnotherUser') })));
                    return;
                }
                const result = await (0, request_1.requestUserUpdate)({
                    id,
                    data: {
                        email: data?.basicFields?.email,
                    },
                });
                if ((0, util_1.isValue)(result) && (0, util_1.isValue)(result.email)) {
                    dispatch((0, user_details_1.updatedUserEmail)(result.email));
                }
            }
        }
        setEditing([]);
        setConfirmEmail('');
    };
    const { fields } = (0, react_hook_form_1.useFieldArray)({ control, name: 'customAttributes' });
    const watchedEmail = (0, react_hook_form_1.useWatch)({ control, name: 'basicFields.email' });
    const isEmailEdited = (0, util_1.isDefined)(watchedEmail) && watchedEmail !== processedData.email;
    const emailConfirmMismatch = isEmailEdited && confirmEmail !== watchedEmail;
    const { displayContent } = (0, useEmployeeCardFooterContent_1.useEmployeeCardFooterContent)(react_1.default.createElement("div", { style: { display: 'flex' } },
        react_1.default.createElement(ui_1.Button, { onClick: () => {
                closeModal();
                // dispatch(setModal(null))
            } }, (0, util_1.t)('CLOSE')),
        react_1.default.createElement(ui_1.Button, { onClick: handleSubmit(onSubmit), style: 'CONTAINED', disabled: editing.length === 0 || isSubmitting || emailConfirmMismatch }, (0, util_1.t)('SAVE'))));
    const emailIsBad = (0, email_is_default_1.emailIsDefault)(employee.user?.email ?? '');
    const generateAttributeCell = react_1.default.useCallback((attr) => {
        if (attr?.type === graphql_1.CustomAttributeValueType.Multiselect) {
            return {
                label: attr.name ?? '-',
                value: attr.value?.join(', '),
            };
        }
        if (attr?.type === graphql_1.CustomAttributeValueType.Boolean) {
            if ((0, util_1.isUndefined)(attr.value) || (0, util_1.isNull)(attr.value)) {
                return {
                    label: '-',
                    value: null,
                };
            }
            return {
                label: attr.name ?? '-',
                value: attr.value ? (0, util_1.t)('YES') : (0, util_1.t)('NO'),
            };
        }
        if (attr?.type === graphql_1.CustomAttributeValueType.Date) {
            return {
                label: attr.name ?? '-',
                value: (0, moment_1.default)(attr.value, 'YYYY-MM-DD').isValid()
                    ? (0, moment_1.default)(attr.value, 'YYYY-MM-DD').format('DD.MM.YYYY')
                    : null,
            };
        }
        return {
            label: attr.name ?? '-',
            value: attr.value,
        };
    }, []);
    return (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement(ui_1.Spacing, { size: ui_1.Spacing.SIZES.SIZE_16, type: ui_1.Spacing.TYPES.VERTICAL },
            (0, util_1.isDefined)((0, maybe_unwrap_1.unwrapMaybe)(customData?.lastUpdateFromPlugin)) && (react_1.default.createElement(ui_1.Alert, { size: 'FULL_WIDTH', type: 'INFO', text: (0, util_1.t)('LAST_AUTOMATIC_DATA_UPDATE', {
                    date_time: (0, moment_1.default)(customData?.lastUpdateFromPlugin).format('DD.MM.YYYY, HH:mm'),
                }) })),
            react_1.default.createElement(ui_1.Flex, { direction: ui_1.Flex.DIRECTION.COLUMN },
                react_1.default.createElement("div", { style: {
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    } },
                    react_1.default.createElement(ui_1.Text, { type: 'header', weight: 'bold' }, (0, util_1.t)('BASIC_INFO')),
                    react_1.default.createElement(ToggleEdit, { type: 'basicFields', editing: editing, setEditing: setEditing, hidden: !allowEditBasicFields })),
                react_1.default.createElement(ui_1.Spacing, { type: ui_1.Spacing.TYPES.VERTICAL, size: ui_1.Spacing.SIZES.SIZE_16 }),
                !editing.includes('basicFields') && (react_1.default.createElement(data_grid_1.DataGrid, { columns: [
                        {
                            cells: [
                                {
                                    label: (0, util_1.t)('LAST_NAME'),
                                    value: processedData.lastName,
                                },
                                {
                                    label: (0, util_1.t)('EMAIL'),
                                    value: employee.user?.dummy || emailIsBad
                                        ? null
                                        : processedData.email,
                                },
                                {
                                    label: (0, util_1.t)('EMPLOYEE_PROFILE_ADDRESS'),
                                    value: customData?.address,
                                },
                                {
                                    label: (0, util_1.t)('PERSONAL_ID'),
                                    value: customData?.employeeId,
                                },
                            ],
                        },
                        {
                            cells: [
                                {
                                    label: (0, util_1.t)('FIRST_NAME'),
                                    value: processedData.firstName,
                                },
                                {
                                    label: (0, util_1.t)('PHONE'),
                                    value: util_1.miscUtil.formatPhoneNumber(processedData.telephone),
                                },
                                {
                                    label: (0, util_1.t)('EMPLOYEE_PROFILE_BIRTHDATE'),
                                    value: processedData.birthdate
                                        ? (0, moment_1.default)(processedData.birthdate).format('DD.MM.YYYY')
                                        : null,
                                },
                            ],
                        },
                    ] })),
                editing.includes('basicFields') && (react_1.default.createElement("div", { key: 'editing-basic-fields', style: {
                        display: 'grid',
                        gridTemplateRows: 'auto',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '0.5rem',
                    } },
                    react_1.default.createElement(text_input_1.TextInput, { control: control, name: 'basicFields.lastName', label: (0, util_1.t)('LAST_NAME'), disabled: isPreventedByPlugin('editEmployeeDataName') ||
                            keepBasicFieldsDisabled }),
                    react_1.default.createElement(text_input_1.TextInput, { control: control, name: 'basicFields.firstName', label: (0, util_1.t)('FIRST_NAME'), disabled: isPreventedByPlugin('editEmployeeDataName') ||
                            keepBasicFieldsDisabled }),
                    react_1.default.createElement(text_input_1.TextInput, { control: control, name: 'basicFields.email', label: (0, util_1.t)('EMAIL'), disabled: (!userIsMe && !employee?.user?.dummy) || !!employee?.user?.dummy }),
                    react_1.default.createElement(ui_1.Input, { key: 'confirm-email-field', label: (0, util_1.t)('CONFIRM_EMAIL'), value: confirmEmail, disabled: !isEmailEdited, onChange: setConfirmEmail, errorMessage: (0, maybe_unwrap_1.unwrapMaybe)((0, util_1.t)('EMAIL_MISMATCH')), hasError: emailConfirmMismatch, size: 'full-width', onKeyDown: (e) => {
                            if (e.ctrlKey && e.key === 'v') {
                                e.preventDefault();
                                e.stopPropagation();
                            }
                        } }),
                    react_1.default.createElement(forms_1.PhoneInput, { control: control, name: 'basicFields.telephone', label: (0, util_1.t)('PHONE') }),
                    react_1.default.createElement(text_input_1.TextInput, { control: control, name: 'basicFields.address', label: (0, util_1.t)('EMPLOYEE_PROFILE_ADDRESS'), disabled: isPreventedByPlugin('editEmployeeDataAddress') ||
                            keepFieldsDisabled }),
                    react_1.default.createElement(forms_1.DateInput, { control: control, name: 'basicFields.birthdate', label: (0, util_1.t)('EMPLOYEE_PROFILE_BIRTHDATE'), disabled: isPreventedByPlugin('editEmployeeDataBirthDate') ||
                            keepFieldsDisabled }),
                    react_1.default.createElement(text_input_1.TextInput, { control: control, name: 'basicFields.employeeId', label: (0, util_1.t)('PERSONAL_ID'), disabled: isPreventedByPlugin('editEmployeeDataEmployeeId') ||
                            keepFieldsDisabled }))),
                react_1.default.createElement(ui_1.Spacing, { type: ui_1.Spacing.TYPES.VERTICAL, size: ui_1.Spacing.SIZES.SIZE_16 }),
                react_1.default.createElement("div", { style: {
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    } },
                    react_1.default.createElement(ui_1.Text, { type: 'header', weight: 'bold' }, (0, util_1.t)('CUSTOM_ATTRIBUTES')),
                    react_1.default.createElement("div", { style: { display: 'flex', gap: '1rem' } },
                        react_1.default.createElement("div", { onClick: () => {
                                util_1.routeUtil.navigate('/workspace/custom-attributes');
                            }, style: {
                                display: 'flex',
                                gap: '0.5rem',
                                alignItems: 'center',
                                cursor: 'pointer',
                            } },
                            react_1.default.createElement(ui_1.Icon, { color: 'primary', ico: 'settings2', size: 'small' }),
                            react_1.default.createElement(ui_1.Text, { color: 'primary', type: 'header', weight: 'bold' }, (0, util_1.t)('MANAGE'))),
                        react_1.default.createElement(ToggleEdit, { type: 'customAttributes', editing: editing, setEditing: setEditing, hidden: mappedCustomAttributes?.length === 0 || keepFieldsDisabled }))),
                react_1.default.createElement(ui_1.Spacing, { type: ui_1.Spacing.TYPES.VERTICAL, size: ui_1.Spacing.SIZES.SIZE_16 }),
                !editing.includes('customAttributes') && (react_1.default.createElement(data_grid_1.DataGrid, { columns: [
                        {
                            cells: columns.left?.map(generateAttributeCell),
                        },
                        {
                            cells: columns.right?.map(generateAttributeCell),
                        },
                    ] })),
                editing.includes('customAttributes') && (react_1.default.createElement("div", { style: {
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '0.5rem',
                    } },
                    react_1.default.createElement(builder_1.Builder, { elements: fields, getKey: (el) => el.id }, (field, index) => {
                        if (field.type === graphql_1.CustomAttributeValueType.Number) {
                            return (react_1.default.createElement(forms_1.NumberInput, { control: control, name: `customAttributes.${index}.value`, label: (0, maybe_unwrap_1.unwrapMaybe)(userAttributes?.find((e) => e?.id === field.attributeId)
                                    ?.name) }));
                        }
                        if (field.type === graphql_1.CustomAttributeValueType.String) {
                            return (react_1.default.createElement(text_input_1.TextInput, { control: control, name: `customAttributes.${index}.value`, label: (0, maybe_unwrap_1.unwrapMaybe)(userAttributes?.find((e) => e?.id === field.attributeId)
                                    ?.name) }));
                        }
                        if (field.type === graphql_1.CustomAttributeValueType.Date) {
                            return (react_1.default.createElement(forms_1.DateInput, { control: control, name: `customAttributes.${index}.value`, label: (0, maybe_unwrap_1.unwrapMaybe)(userAttributes?.find((e) => e?.id === field.attributeId)
                                    ?.name) }));
                        }
                        if (field.type === graphql_1.CustomAttributeValueType.Select) {
                            return (react_1.default.createElement(dropdown_input_1.DropdownInput, { control: control, name: `customAttributes.${index}.value`, options: field.values, label: (0, maybe_unwrap_1.unwrapMaybe)(userAttributes?.find((e) => e?.id === field.attributeId)
                                    ?.name) }));
                        }
                        if (field.type === graphql_1.CustomAttributeValueType.Multiselect) {
                            return (react_1.default.createElement(dropdown_multi_input_1.DropdownMultiInput, { control: control, name: `customAttributes.${index}.value`, options: field.values, transformer: {
                                    in: (v) => {
                                        return field.values?.filter((e) => (0, util_1.isDefined)(e.value) ? v?.includes(e.value) : false);
                                    },
                                    out: (selected) => {
                                        return selected.map((e) => e.value);
                                    },
                                }, label: (0, maybe_unwrap_1.unwrapMaybe)(userAttributes?.find((e) => e?.id === field.attributeId)
                                    ?.name) }));
                        }
                        if (field.type === graphql_1.CustomAttributeValueType.Boolean) {
                            return (react_1.default.createElement("div", { style: { display: 'flex', flexDirection: 'column' } },
                                react_1.default.createElement(input_label_1.InputLabel, { label: (0, maybe_unwrap_1.unwrapMaybe)(userAttributes?.find((e) => e?.id === field.attributeId)?.name) ?? '' }),
                                react_1.default.createElement("div", { style: {
                                        paddingLeft: '0.5rem',
                                        height: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        transform: 'translateY(-1px)',
                                    } },
                                    react_1.default.createElement(checkbox_1.CheckboxInput, { control: control, name: `customAttributes.${index}.value` }))));
                        }
                    }))))),
        displayContent()));
};
exports.DataSection = DataSection;
